import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";


const collegeRegions = {
  "Southwest": [
    "234933", // Clark College
    "234845", // Centralia College
    "235334", // Grays Harbor College
    "235750", // Lower Columbia College
    "236656", // South Puget Sound Community College
    "WASTBTC" // SBCTC (State Board) — placeholder if needed
  ],
  "Seattle Metro": [
    "234669", // Bellevue College
    "439190", // Cascadia College
    "235699", // Lake Washington Institute of Technology
    "236382", // Renton Technical College
    "236072", // North Seattle College
    "236513", // Seattle Central College
    "236504"  // South Seattle College
  ],
  "Northwest": [
    "234696", // Bellingham Technical College
    "235103", // Edmonds College
    "235149", // Everett Community College
    "236638", // Skagit Valley College
    "236610", // Shoreline Community College
    "237039", // Whatcom Community College
    "236258"  // Peninsula College
  ],
  "Eastern Washington": [
    "234711", // Big Bend Community College
    "234979", // Columbia Basin College
    "236975", // Wenatchee Valley College
    "236692", // Spokane Community College
    "236708", // Spokane Falls Community College
    "236887", // Walla Walla Community College
    "237109"  // Yakima Valley College
  ],
  "South Central": [
    "235671", // Bates Technical College
    "234951", // Clover Park Technical College
    "235343", // Green River College
    "235431", // Highline College
    "236188", // Olympic College
    "235237", // Pierce College District
    "236753"  // Tacoma Community College
  ]
};


// Define Area Groups
const areaGroups = {
  "Great Northwest": [
    "18303", "18100", "05401", "18401", "16049", "05313", "18400", "05121",
    "16050", "16048", "05903", "05402", "05323", "18402", "18902",
    "Washington Military Department", "West Sound Technical Skills Center"
  ],
  "KC-CTA": [
    "17408", "17405", "17216", "17210", "17401", "17411", "17415", "17414",
    "17400", "17417", "Puget Sound Skills Center", "17403", "17407", "17001",
    "Seattle Skills Center", "17412", "17410", "17409", "17406", "17402",
    "17903", "17910", "17404", "Washington Network for Innovative Careers",
    "Washington Network for Innovative Careers Skill Center"
  ],
  "North Central": [
    "24111", "09075", "04228", "04222", "19404", "Columbia Basin Technical Skills Center", "13151", "09206",
    "19401", "04127", "13165", "13301", "Inchelium School District", "19403",
    "04129", "09207", "04019", "24350", "13161", "24014", "24105", "24019",
    "09013", "24410", "24122", "04901", "13144", "13160", "13156", "19400",
    "24404", "13073", "13146", "09209", "04246", "Wenatchee Valley Technical Skills Center",
    "13167"
  ],
  "Northeastern": [
    "32356", "32360", "33036", "38300", "38306", "33206", "33115", "22073",
    "10050", "26059", "22207", "32414", "32361", "32358", "38302", "22204",
    "Innovation Spokane Charter School District", "33212", "38126", "32362",
    "33207", "32354", "32326", "26056", "32325", "33211", "38324", "22105",
    "38301", "32907", "38267", "22009", "10309", "32416", "38320", "26070",
    "32081", "Spokane Area Professional-Technical Skills Center", "Spokane Valley Tech",
    "22008", "38322", "38265", "32363", "22200"
  ],
  "Northern": [
    "29103", "31016", "37501", "37503", "29100", "29011", "15204", "31330",
    "31015", "31002", "37502", "31332", "29311", "31004", "31306", "28144",
    "37504", "31025", "37505", "31103", "37507", "29320", "31006", "37506",
    "Northwest Career & Technical Academy", "15201", "28137", "28149", "29101",
    "Sno-Isle Skills Center", "31201", "15206", "31401", "31311"
  ],
  "Pierce": [
    "27403", "27901", "27400", "27343", "27404", "27417", "27402", "27344",
    "27401", "Pierce County Skills Center", "27003", "27001", "27320", "27010",
    "27083", "27416"
  ],
  "Southeast-South Central": [
    "02420", "02250", "36250", "36400", "07002", "39090", "03053", "20404",
    "39200", "39204", "39203", "11056", "03017", "03052", "01158", "20406",
    "39120", "39209", "39003", "11051", "01147", "11001", "12110", "36402",
    "03116", "03400", "39119", "39201", "39202", "36300", "Tri-Tech Skills Center",
    "36401", "36140", "39207", "01109", "33049", "39208", "20094", "39007",
    "Yakima Valley Technical Skills Center", "39205", "01160", "SE AREA TECHNICAL SKILLS CENTER",
    "20405"
  ],
  "Southwest": [
    "06119", "06117", "Cascadia Technical Academy Skills Center", "08401",
    "06114", "06098", "08402", "08458", "06101", "08122", "30031", "25116",
    "06122", "Rooted School Vancouver", "25118", "30303", "08130", "06037",
    "35200", "06112", "08404"
  ],
  "Western": [
    "14005", "21226", "21234", "21401", "21302", "14068", "14028", "14097",
    "23311", "14066", "21214", "21206", "21014", "25155", "New Market Skills Center",
    "14064", "23403", "34003", "14400", "25101", "14172", "34111", "21300",
    "21301", "34307", "34401", "23309", "14077", "34402", "21237", "34033",
    "Twin Harbors - A Branch of New Market Skills Center", "21303", "25160",
    "21232", "14117", "34002"
  ]
};

// Build districtCodeToAreaGroup from areaGroups
const districtCodeToAreaGroup = {};
Object.entries(areaGroups).forEach(([groupName, codes]) => {
  codes.forEach((code) => {
    districtCodeToAreaGroup[code] = groupName;
  });
});

const rowVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.8, 0.25, 1],
      delay: custom.index * 0.02,
    },
  }),
  exit: (custom) => ({
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.1,
      ease: [0.25, 0.8, 0.25, 1],
      delay: (custom.totalItems - custom.index - 1) * 0.01,
    },
  }),
};

// Animated Area Group Header
const AnimatedAreaGroupHeader = ({ areaGroup, colSpan, totals, index, totalItems }) => (
  <motion.tr
    className="bg-gray-50 border-b border-indigo-400"
    variants={rowVariants}
    initial="hidden"
    animate="visible"
    exit="exit"
    custom={{ index, totalItems }}
  >
    <td
      colSpan={colSpan}
      className="pt-8 px-4 py-0 text-left uppercase text-xs font-semibold text-indigo-700"
    >
      {areaGroup}
    </td>
    {totals &&
      totals.map((total, idx) => (
        <td
          key={idx}
          className="pt-8 px-4 py-0 text-right uppercase text-xs font-semibold text-indigo-700"
        >
          {total.toLocaleString()}
        </td>
      ))}
  </motion.tr>
);

// Animated Table Row
const AnimatedTableRow = ({ item, index, totalItems }) => (
  <motion.tr
    className="hover:bg-gray-100 border-b border-gray-200"
    variants={rowVariants}
    initial="hidden"
    animate="visible"
    exit="exit"
    custom={{ index, totalItems }}
  >
    <td className="px-4 py-2 text-left truncate" title={item.districtName}>
      {item.districtName}
    </td>
    {item.All.map((val, idx) => (
      <td key={idx} className="px-4 py-2 text-right">
        {val.toLocaleString()}
      </td>
    ))}
    <td className="font-medium px-4 py-2 text-right">
      {item.All.reduce((sum, value) => sum + value, 0).toLocaleString()}
    </td>
  </motion.tr>
);

// Row for "No Enrollment" scenario
const NoEnrollmentRow = ({ years }) => {
  const cols = years.length + 1;
  return (
    <tr className="border-b border-gray-200 hover:bg-gray-100">
      <td className="px-4 py-2 text-left text-gray-500 italic">
        No Enrollment
      </td>
      {Array(cols).fill(0).map((_, idx) => (
        <td key={idx} className="px-4 py-2 text-right text-gray-500">0</td>
      ))}
    </tr>
  );
};

export default function CipEnrollmentsOthers({ cipCatalog, defaultConsortium, cip }) {
  const [expanded, setExpanded] = useState(false);

  // Use cipDetails.cipName if available, otherwise fall back to cip (for totals)
  const cipName = cipCatalog?.cipDetails?.cipName || cip;
  const [lastCIPName, setLastCIPName] = useState(cipName);

  useEffect(() => {
    if (lastCIPName !== cipName) {
      setExpanded(false);
      setLastCIPName(cipName);
    }
  }, [cipName, lastCIPName]);

  if (!cipCatalog || !cipCatalog.cipEnrollment || !defaultConsortium) {
    return (
      <div className="bg-gray-50 pt-4 pb-4 text-center text-gray-500">
        Loading...
      </div>
    );
  }

  const { cipEnrollment } = cipCatalog;
  const years = ["2021", "2022", "2023", "2024", "2025"];
  const totalColumns = 1 + years.length + 1; // District Name + years + Totals

  // Determine home consortium area group from defaultConsortium
  let foundHomeGroup = null;
  for (let distName of defaultConsortium) {
    const homeDistrict = cipEnrollment.find((d) => d.districtName === distName);
    if (homeDistrict) {
      const group = districtCodeToAreaGroup[homeDistrict.districtCode];
      if (group) {
        foundHomeGroup = group;
        break;
      }
    }
  }
  
  const homeAreaGroup = foundHomeGroup ? `Home Area Group (${foundHomeGroup})` : "Home Area Group";

  // Initialize all groups with empty arrays
  const allGroups = Object.keys(areaGroups);
  const groupsMap = {};
  allGroups.forEach((group) => {
    groupsMap[group] = [];
  });
  // Also ensure homeAreaGroup key is present
  if (!groupsMap[homeAreaGroup]) {
    groupsMap[homeAreaGroup] = [];
  }

  let statewideItem = null;

  // Assign each district to its area group
  cipEnrollment.forEach((item) => {
    // Check if this is the statewide row
    const isStatewide = item.districtName === "Statewide" || item.districtCode === "Statewide";
    if (isStatewide) {
      statewideItem = item;
      return; // Don't add Statewide to any group
    }

    const group = districtCodeToAreaGroup[item.districtCode] || "Other";
    if (defaultConsortium.includes(item.districtName)) {
      item.areaGroup = homeAreaGroup; 
    } else {
      item.areaGroup = group;
    }
    if (!groupsMap[item.areaGroup]) {
      groupsMap[item.areaGroup] = [];
    }
    groupsMap[item.areaGroup].push(item);
  });

  // If "Other" exists and we don't want to show it, remove it
  delete groupsMap["Other"];

  // If we found a home group and it's one of the defined groups, 
  // remove the original foundHomeGroup from groupsMap to avoid duplication.
  if (foundHomeGroup && groupsMap[foundHomeGroup]) {
    // All districts that belonged to foundHomeGroup should now be in homeAreaGroup, 
    // or if none were there, foundHomeGroup is empty anyway.
    delete groupsMap[foundHomeGroup];
  }

  // Prepare the groups list
  let groups = Object.keys(groupsMap);

  // Move home area group to the front if it exists
  const homeIndex = groups.indexOf(homeAreaGroup);
  if (homeIndex > -1) {
    groups.splice(homeIndex, 1);
    groups.unshift(homeAreaGroup);
  }

  // Handle Statewide totals if present
  let statewideTotals = null;
  if (statewideItem) {
    statewideTotals = [
      ...statewideItem.All,
      statewideItem.All.reduce((sum, v) => sum + v, 0)
    ];
  }

  // Identify other groups (excluding home area group)
  let otherGroups;
  if (homeIndex > -1) {
    otherGroups = groups.slice(1);
  } else {
    otherGroups = groups;
  }

  // Sort other groups alphabetically
  otherGroups.sort();

  const firstOtherGroup = otherGroups[0];

  // Initial display count for first other group
  const initialDisplayCount = 2;

  // Calculate total number of districts in other groups
  const totalOtherDistricts = groups
    .filter((g) => g !== homeAreaGroup)
    .reduce((sum, g) => sum + groupsMap[g].length, 0);

  const initialOtherDistricts = firstOtherGroup
    ? Math.min(initialDisplayCount, groupsMap[firstOtherGroup].length)
    : 0;

  const hasMore = totalOtherDistricts > initialOtherDistricts;

  // Calculate totals per group
  const groupTotals = {};
  groups.forEach((group) => {
    const groupItems = groupsMap[group];
    if (groupItems && groupItems.length > 0) {
      const totals = new Array(years.length + 1).fill(0); // Years + Total
      groupItems.forEach((item) => {
        item.All.forEach((val, idx) => {
          totals[idx] += val;
        });
        totals[totals.length - 1] += item.All.reduce((sum, value) => sum + value, 0);
      });
      groupTotals[group] = totals;
    } else {
      // No enrollment, so totals are zeros
      groupTotals[group] = new Array(years.length + 1).fill(0);
    }
  });

  // Build expandable rows
  const expandableRows = [];
  let index = 0;
  if (expanded) {
    // Add remaining districts from first other group (if any)
    if (firstOtherGroup) {
      const remaining = groupsMap[firstOtherGroup].slice(initialDisplayCount);
      if (remaining.length > 0) {
        remaining.forEach((item) => {
          expandableRows.push(
            <AnimatedTableRow
              key={item.districtName}
              item={item}
              index={index++}
              totalItems={totalOtherDistricts}
            />
          );
        });
      } else if (groupsMap[firstOtherGroup].length === 0) {
        expandableRows.push(<NoEnrollmentRow key={`noenroll-${firstOtherGroup}`} years={years} />);
      }
    }

    // Add remaining other groups
    otherGroups.slice(1).forEach((group) => {
      expandableRows.push(
        <AnimatedAreaGroupHeader
          key={`header-${group}`}
          areaGroup={group}
          colSpan={1}
          totals={groupTotals[group]}
          index={index++}
          totalItems={totalOtherDistricts}
        />
      );
      if (groupsMap[group].length > 0) {
        groupsMap[group].forEach((item) => {
          expandableRows.push(
            <AnimatedTableRow
              key={item.districtName}
              item={item}
              index={index++}
              totalItems={totalOtherDistricts}
            />
          );
        });
      } else {
        // No enrollment row
        expandableRows.push(<NoEnrollmentRow key={`noenroll-${group}`} years={years} />);
      }
    });
  }

  return (
    <div className="bg-white rounded-lg pt-4 mx-auto pb-4">
      {cipCatalog && cipName && (
        <div className="mx-4">
          <h3 className="text-2xl leading-7 mt-7 text-slate-700">
            {`Other Districts Enrolled in ${cipName}`}
          </h3>
          <div className="overflow-x-auto relative px-8 pb-8 mt-6">
            <div className="relative">
              <table className="w-full table-fixed border-collapse text-xs text-left text-gray-800 bg-white">
                <colgroup>
                  <col style={{ width: "40%" }} />
                  {Array(years.length + 1)
                    .fill(null)
                    .map((_, idx) => (
                      <col key={idx} style={{ width: `${60 / (years.length + 1)}%` }} />
                    ))}
                </colgroup>
                <thead>
                  <tr className="text-xs font-semibold text-gray-600 bg-gray-50">
                    <th className="px-4 py-2 text-left truncate" title="District Name">
                      District Name
                    </th>
                    {years.map((year) => (
                      <th key={year} className="px-4 py-2 text-right">
                        {year}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-right">Totals</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Home Area Group */}
                  {groups.includes(homeAreaGroup) && (
                    <>
                      <AnimatedAreaGroupHeader
                        areaGroup={homeAreaGroup}
                        colSpan={1}
                        totals={groupTotals[homeAreaGroup]}
                      />
                      {groupsMap[homeAreaGroup].length > 0 ? (
                        groupsMap[homeAreaGroup].map((item) => (
                          <tr
                            key={item.districtName}
                            className="border-b border-gray-200 hover:bg-gray-100"
                          >
                            <td className="px-4 py-2 text-left truncate" title={item.districtName}>
                              {item.districtName}
                            </td>
                            {item.All.map((val, idx) => (
                              <td key={idx} className="px-4 py-2 text-right">
                                {val.toLocaleString()}
                              </td>
                            ))}
                            <td className="font-medium text-gray-900 px-4 py-2 text-right">
                              {item.All.reduce((sum, v) => sum + v, 0).toLocaleString()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <NoEnrollmentRow years={years} />
                      )}
                      {/* Spacer */}
                      <tr>
                        <td colSpan={totalColumns} className="py-7 bg-zinc-50"></td>
                      </tr>
                    </>
                  )}

                  {/* Statewide Totals (if present) */}
                  {statewideTotals && (
                    <>
                      <tr className="bg-zinc-50 border-b border-gray-400 hover:bg-zinc-100">
                        <td className="font-extrabold uppercase px-4 py-2 text-left">
                          Statewide Total Enrollment
                        </td>
                        {statewideTotals.map((val, idx) => (
                          <td key={idx} className="font-extrabold px-4 py-2 text-right">
                            {val.toLocaleString()}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td colSpan={totalColumns} className="py-7 bg-zinc-50"></td>
                      </tr>
                    </>
                  )}

{firstOtherGroup && (
  <>
    <AnimatedAreaGroupHeader
      areaGroup={firstOtherGroup}
      colSpan={1}
      totals={groupTotals[firstOtherGroup]}
    />

    {/* Only display the initial items if any exist.
       If it's empty, don't render NoEnrollmentRow here! */}
    {groupsMap[firstOtherGroup].length > 0 && (
      groupsMap[firstOtherGroup].slice(0, initialDisplayCount).map((item) => (
        <tr
          key={item.districtName}
          className="border-b border-gray-200 hover:bg-gray-100"
        >
          <td className="px-4 py-2 text-left truncate" title={item.districtName}>
            {item.districtName}
          </td>
          {item.All.map((val, idx) => (
            <td key={idx} className="px-4 py-2 text-right">
              {val.toLocaleString()}
            </td>
          ))}
          <td className="font-medium text-gray-900 px-4 py-2 text-right">
            {item.All.reduce((sum, v) => sum + v, 0).toLocaleString()}
          </td>
        </tr>
      ))
    )}
  </>
)}

                  {/* Expandable Rows */}
                  <AnimatePresence>{expanded && expandableRows}</AnimatePresence>
                </tbody>
              </table>

              {/* Gradient overlay if not expanded */}
              {!expanded && hasMore && (
                <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none bg-gradient-to-t from-white to-transparent" />
              )}
            </div>

            {/* Show More / Show Less Button */}
            {hasMore && (
              <button
                className="w-full flex flex-col items-center justify-center py-6 bg-zinc-50 rounded-b-lg bg-gradient-to-b from-white to-transparent hover:bg-none hover:bg-zinc-100/40 transition duration-200 ease-in-out group border-b-4 border-zinc-100 hover:border-zinc-100"
                onClick={() => setExpanded(!expanded)}
              >
                <div
                  className={`flex items-center justify-center w-12 h-12 bg-white group-hover:bg-zinc-200/70 rounded-full text-blue-600 transition-transform transform duration-200 ease-in-out ${
                    expanded ? "rotate-180" : ""
                  }`}
                >
                  {/* Chevron Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="w-6 h-6"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                  </svg>
                </div>
                <span className="mt-2 text-blue-600 font-medium transition duration-200 group-hover:underline">
                  {expanded ? "Show Less" : "Show More"}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}